/**
 * BOQ Totals — single source of truth.
 *
 * iter-312o: Email body / Email preview / Edit BOQ summary / Public BOQ / PDF / Excel
 * MUST all derive their summary numbers from this one function. Any deviation
 * (different cascade order, different rate-field fallback, etc.) shows up as
 * mismatched grand totals between the same BOQ across surfaces — which is the
 * exact bug we are fixing here.
 *
 * Cascade (matches Kitchen Estimator waterfall + Edit BOQ summary panel):
 *
 *   Basic Total = sum(qty × rate)              ← per-line, headers/discount rows skipped
 *   − Discount                                  ← value or % of Basic
 *   PostDiscount = Basic − Discount
 *   + Freight   = PostDiscount × freight%
 *   + Labour    = PostDiscount × labour%
 *   + Consult.  = PostDiscount × consult%  (or fixed value if consultancyType='amount'|'fixed')
 *   PreGST      = PostDiscount + Overheads
 *   GST         = PreGST × weightedGstRate   (weightedGstRate = Σ(qty×rate×gst%) / Basic)
 *   Grand Total = PreGST + GST                  ← gross, invoice-style — same on Est & Act
 *   − Client Payments Applied                   ← Actual only (real cash already received)
 *   − Credit Notes Applied                      ← Actual only (refunds/adjustments issued)
 *   Payment Receivable = Grand Total − Payments − CreditNotes   (Actual side; Est side = Grand)
 *
 * Actual cascade mirrors the same formula with `actual_*` rate/qty fields and the same
 * GST percentages.
 *
 * Rate selection (so the layer the user picked = the layer used in totals):
 *   - 'vendor'  → item.vendor_rate ?? item.rate
 *   - 'admin'   → item.admin_rate  ?? item.rate
 *   - 'client'  → item.client_rate ?? item.rate
 *   - 'auto'    → CtV worksheets use client_rate, V→C worksheets use vendor_rate
 *
 * Actual rate fallback (per layer):
 *   actual_<layer>_rate ?? actual_rate ?? <est rate>
 */

export function pickRate(item, layer) {
  switch (layer) {
    case 'vendor':
      return parseFloat(item.vendor_rate ?? item.rate ?? 0) || 0;
    case 'admin':
      return parseFloat(item.admin_rate ?? item.rate ?? 0) || 0;
    case 'client':
    default:
      return parseFloat(item.client_rate ?? item.rate ?? 0) || 0;
  }
}

export function pickActualRate(item, layer, estRate) {
  const fallback = estRate;
  switch (layer) {
    case 'vendor':
      return parseFloat(item.actual_vendor_rate ?? item.actual_rate ?? fallback) || 0;
    case 'admin':
      return parseFloat(item.actual_admin_rate ?? item.actual_rate ?? fallback) || 0;
    case 'client':
    default:
      return parseFloat(item.actual_client_rate ?? item.actual_rate ?? fallback) || 0;
  }
}

/**
 * Compute summary totals for a BOQ.
 *
 * @param {Array}  items                       — worksheet items (headers + discount rows are skipped)
 * @param {Object} opts
 * @param {string} opts.layer                  — 'vendor' | 'admin' | 'client' | 'auto'
 * @param {string} opts.pricingModel           — 'client_to_vendor' | 'vendor_to_client' (only used when layer='auto')
 * @param {string} opts.discountType           — 'percentage' | 'value'
 * @param {number} opts.discountValue          — % when type='percentage', INR when 'value'
 * @param {number} opts.freightPct             — overhead % on PostDiscount
 * @param {number} opts.labourHandlingPct      — overhead % on PostDiscount
 * @param {string} opts.consultancyType        — 'percentage' | 'amount' | 'fixed'
 * @param {number} opts.consultancyPct         — % when type='percentage'
 * @param {number} opts.consultancyValue       — INR when type='amount'|'fixed'
 * @param {number} opts.clientPaymentsApplied  — INR already received from client (Actual only)
 * @param {number} opts.creditNotesApplied     — INR credit notes / adjustments issued (Actual only)
 *
 * @returns {Object} — { estBasic, actBasic, discount, actDiscount, freight, labourHandling,
 *                       consultancy, actFreight, actLabour, actConsultancy,
 *                       gst, actGst, estGrand, actGrand, clientPaymentsApplied, creditNotesApplied,
 *                       weightedGstRate, ... }
 */
export const CONSULTANCY_STAGE_PCTS = { 1: 10, 2: 25, 3: 50, 4: 80, 5: 100 };

// Milestone billable consultancy: stages 1-3 cumulative %, 4-5 = higher of milestone % vs weighted site progress %.
export function computeConsultancyMilestone(stageRaw, items, estConsultancy, actConsultancy) {
  const stage = Number(stageRaw) || 0;
  const milestonePct = CONSULTANCY_STAGE_PCTS[stage] || 0;
  let sitePct = 0;
  if (stage >= 4) {
    const rows = (items || []).filter((it) => it && !(it.is_header || it.item_type === 'header' || it.is_discount));
    const totalAmt = rows.reduce((s, it) => s + (Number(it.amount) || 0), 0);
    if (totalAmt > 0) sitePct = rows.reduce((s, it) => s + (Number(it.amount) || 0) * (Number(it.task_progress) || 0), 0) / totalAmt;
  }
  const effectivePct = stage >= 4 ? Math.max(milestonePct, sitePct) : milestonePct;
  return {
    stage,
    milestonePct,
    sitePct,
    effectivePct,
    est: (Number(estConsultancy) || 0) * effectivePct / 100,
    act: (Number(actConsultancy) || 0) * effectivePct / 100,
  };
}

// Billable grand never adds both: when a milestone exists use the higher of consultancy % and milestone.
export function applyMilestoneToBillableGrand(billableGrand, billableConsultancy, milestoneAmt) {
  const m = Number(milestoneAmt) || 0;
  const g = Number(billableGrand) || 0;
  if (m <= 0) return g;
  const c = Number(billableConsultancy) || 0;
  return g - c + Math.max(c, m);
}

export function computeBoqTotals(items, opts = {}) {
  const layerOpt = opts.layer || 'auto';
  const pricingModel = opts.pricingModel || 'vendor_to_client';
  const layer = layerOpt === 'auto'
    ? (pricingModel === 'client_to_vendor' ? 'client' : 'vendor')
    : layerOpt;

  const discountType = opts.discountType || 'value';
  const discountValue = parseFloat(opts.discountValue || 0) || 0;
  // iter-318: 'before_tax' (default) — discount subtracts from Basic, then overheads + GST
  //           'after_tax'  — discount subtracts from the GST-inclusive grand total
  const discountApplyMode = opts.discountApplyMode === 'after_tax' ? 'after_tax' : 'before_tax';
  const freightPct = parseFloat(opts.freightPct || 0) || 0;
  const labourPct = parseFloat(opts.labourHandlingPct || 0) || 0;
  const consultancyType = opts.consultancyType || 'percentage';
  const consultancyPct = parseFloat(opts.consultancyPct || 0) || 0;
  const consultancyValue = parseFloat(opts.consultancyValue || 0) || 0;
  // iter-312p: Actual-only deductions — real cash received and credit notes
  // issued against the linked project. Estimated cascade is unaffected.
  const clientPaymentsApplied = Math.max(0, parseFloat(opts.clientPaymentsApplied || 0) || 0);
  const creditNotesApplied = Math.max(0, parseFloat(opts.creditNotesApplied || 0) || 0);

  let estBasic = 0;
  let actBasic = 0;
  let estGstAccrual = 0; // Σ(qty × rate × gst%/100) used to derive the weighted GST rate
  let actGstAccrual = 0;

  (items || []).forEach((item) => {
    if (!item) return;
    if (item.is_discount || item.item_type === 'header') return;

    const rate = pickRate(item, layer);
    const qty = parseFloat(item.quantity ?? 0) || 0;
    const gstPct = (parseFloat(item.gst_percentage ?? 0) || 0) / 100;
    const aq = item.actual_quantity != null ? (parseFloat(item.actual_quantity) || 0) : qty;
    const ar = pickActualRate(item, layer, rate);

    estBasic += qty * rate;
    estGstAccrual += qty * rate * gstPct;
    actBasic += aq * ar;
    actGstAccrual += aq * ar * gstPct;
  });

  // --- Estimated cascade ---
  // iter-318: discount cascade depends on applyMode.
  // before_tax: Basic − Discount → +Overheads → ×(1+GST) = Grand
  // after_tax:  Basic            → +Overheads → ×(1+GST) − Discount = Grand
  const estDiscountAmount = discountType === 'percentage' && discountValue > 0
    ? estBasic * discountValue / 100
    : (discountValue > 0 ? discountValue : 0);

  const weightedGstRate = estBasic > 0 ? (estGstAccrual / estBasic) : 0;

  let estDiscount, estPostDiscount, estFreight, estLabour, estConsultancy, estOverheads, estPreGst, estGstFinal, estGrand;
  if (discountApplyMode === 'after_tax') {
    estDiscount = estDiscountAmount;
    estFreight = estBasic * freightPct / 100;
    estLabour = estBasic * labourPct / 100;
    estConsultancy = (consultancyType === 'amount' || consultancyType === 'fixed')
      ? consultancyValue
      : estBasic * consultancyPct / 100;
    estOverheads = estFreight + estLabour + estConsultancy;
    estPostDiscount = estBasic; // no pre-tax discount
    estPreGst = estBasic + estOverheads;
    estGstFinal = estPreGst * weightedGstRate;
    estGrand = estPreGst + estGstFinal - estDiscount;
  } else {
    estDiscount = estDiscountAmount;
    estPostDiscount = estBasic - estDiscount;
    estFreight = estPostDiscount * freightPct / 100;
    estLabour = estPostDiscount * labourPct / 100;
    estConsultancy = (consultancyType === 'amount' || consultancyType === 'fixed')
      ? consultancyValue
      : estPostDiscount * consultancyPct / 100;
    estOverheads = estFreight + estLabour + estConsultancy;
    estPreGst = estPostDiscount + estOverheads;
    estGstFinal = estPreGst * weightedGstRate;
    estGrand = estPreGst + estGstFinal;
  }

  // --- Actual cascade (same shape) ---
  const actDiscountAmount = discountType === 'percentage' && discountValue > 0
    ? actBasic * discountValue / 100
    : (discountValue > 0 ? discountValue : 0);
  const actGstRate = actBasic > 0 ? (actGstAccrual / actBasic) : 0;
  let actDiscount, actPostDiscount, actFreight, actLabour, actConsultancy, actOverheads, actPreGst, actGstFinal, actGrand;
  if (discountApplyMode === 'after_tax') {
    actDiscount = actDiscountAmount;
    actFreight = actBasic * freightPct / 100;
    actLabour = actBasic * labourPct / 100;
    actConsultancy = (consultancyType === 'amount' || consultancyType === 'fixed')
      ? consultancyValue
      : actBasic * consultancyPct / 100;
    actOverheads = actFreight + actLabour + actConsultancy;
    actPostDiscount = actBasic;
    actPreGst = actBasic + actOverheads;
    actGstFinal = actPreGst * actGstRate;
    actGrand = actPreGst + actGstFinal - actDiscount;
  } else {
    actDiscount = actDiscountAmount;
    actPostDiscount = actBasic - actDiscount;
    actFreight = actPostDiscount * freightPct / 100;
    actLabour = actPostDiscount * labourPct / 100;
    actConsultancy = (consultancyType === 'amount' || consultancyType === 'fixed')
      ? consultancyValue
      : actPostDiscount * consultancyPct / 100;
    actOverheads = actFreight + actLabour + actConsultancy;
    actPreGst = actPostDiscount + actOverheads;
    actGstFinal = actPreGst * actGstRate;
    actGrand = actPreGst + actGstFinal;
  }
  // iter-312r: Grand Total is now GROSS (invoice-style). Deductions move below it.
  // Payment Receivable = what the client still owes us on the Actual side.
  const actNetReceivable = actGrand - clientPaymentsApplied - creditNotesApplied;

  return {
    layer,
    discountApplyMode,
    // Estimated
    estBasic,
    discount: estDiscount,
    freight: estFreight,
    labourHandling: estLabour,
    consultancy: estConsultancy,
    overheads: estOverheads,
    gst: estGstFinal,
    weightedGstRate,
    estGrand,
    estNetReceivable: estGrand, // No deductions on Estimated side — receivable = grand.
    // Actual
    actBasic,
    actDiscount,
    actFreight,
    actLabour,
    actConsultancy,
    actOverheads,
    actGst: actGstFinal,
    actGstRate,
    actGrand,
    actNetReceivable,
    // iter-312p: surfaced so the UI can display these deductions in their own rows
    clientPaymentsApplied,
    creditNotesApplied,
  };
}
